<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import BaseButton from './BaseButton.vue';

// PROPS
const props = withDefaults(
  defineProps<{
    model?: string | number | null;
    id?: string;
    placeholder?: string;
    disabled?: boolean;
    type?: 'text' | 'number' | 'email';
    readonly?: boolean;
    label?: string;
    name?: string;
    mask?: string;
    hide?: boolean;
    searchable?: boolean;
    clearable?: boolean;
    ariaLabel?: string;
    ariaLabelledby?: string;
    ariaDescribedby?: string;
    autocomplete?: string;
    error?: boolean;
    errorMessage?: string;
    searchButtonAriaLabel?: string;
    searchButtonType?: 'button' | 'submit';
    hideToggleAriaLabel?: string;
  }>(),
  {
    model: '',
    type: 'text',
    searchButtonType: 'button',
  },
);

// EMITS
const emit = defineEmits<{
  (e: 'update', newValue: string | number): void;
  (e: 'toggleEye', hide: boolean): void;
  (e: 'search', localModelValue: string | number): void;
  (e: 'clear'): void;
}>();

// COMPUTED
const localModelValue = computed({
  get() {
    return modelValue.value;
  },
  set(newValue) {
    modelValue.value = newValue;
    emit('update', newValue);
  },
});

const getMask = computed(() => {
  return props.mask;
});

const hideToggleButtonAriaLabel = computed(() => {
  return (
    props.hideToggleAriaLabel ||
    props.ariaLabel ||
    props.label ||
    props.placeholder ||
    props.name ||
    'Toggle visibility'
  );
});

const inputAttrs = computed(() => {
  const attrs: Record<string, string> = {};

  if (props.id) {
    attrs.id = props.id;
  }

  if (props.name) {
    attrs.name = props.name;
  }

  if (props.ariaLabel) {
    attrs['aria-label'] = props.ariaLabel;
  }

  if (props.ariaLabelledby) {
    attrs['aria-labelledby'] = props.ariaLabelledby;
  }

  if (props.ariaDescribedby) {
    attrs['aria-describedby'] = props.ariaDescribedby;
  }

  if (props.autocomplete) {
    attrs.autocomplete = props.autocomplete;
  }

  return attrs;
});

//FUNCTIONS
function clearValue() {
  modelValue.value = null;
}

function toggleEye() {
  isHidden.value = !isHidden.value;
  emit('toggleEye', isHidden.value);
}

function handleEnter() {
  if (props.searchable) {
    search();
  }
}

function handleSearchButtonClick() {
  if (props.searchButtonType !== 'submit') {
    search();
  }
}

function handleUpdate(value: any) {
  if (props.searchable && !value) {
    clear();
  }
}

function clear() {
  emit('clear');
}

function search() {
  emit('search', localModelValue.value ?? '');
}

// REFS
const modelValue = ref<string | number | null>('');
const isHidden = ref<boolean>(true);

// WATCH
watch(
  () => props.model,
  (newValue) => {
    modelValue.value = newValue ?? '';
  },
  { immediate: true },
);

defineExpose({ clearValue });
</script>

<template>
  <q-input
    class="base-inputs"
    outlined
    v-model="localModelValue"
    :placeholder="props.placeholder"
    :disable="props.disabled"
    :readonly="props.readonly"
    :mask="getMask"
    :type="props.type"
    :clearable="props.clearable"
    :error="props.error"
    :error-message="props.errorMessage"
    bottom-slots
    v-bind="inputAttrs"
    @keyup.enter="handleEnter()"
    @clear="clear()"
    @update:model-value="handleUpdate"
  >
    <template v-slot:append v-if="props.hide">
      <q-btn
        flat
        round
        dense
        type="button"
        class="base-inputs__toggle-button"
        :aria-label="hideToggleButtonAriaLabel"
        :aria-pressed="!isHidden"
        @click="toggleEye()"
      >
        <q-icon :name="isHidden ? 'visibility_off' : 'visibility'" aria-hidden="true" />
      </q-btn>
    </template>
    <template v-slot:append v-if="props.searchable">
      <BaseButton
        :type="props.searchButtonType"
        :aria-label="props.searchButtonAriaLabel || props.ariaLabel || props.placeholder"
        @click="handleSearchButtonClick"
      >
        <q-icon name="search" class="cursor-pointer" aria-hidden="true" />
      </BaseButton>
    </template>
  </q-input>
</template>

<style lang="scss">
.base-inputs {
  width: 100%;

  &__toggle-button {
    color: $color-neutral-black;

    &:hover {
      background-color: $color-neutral-grey-200;
    }
  }

  &.q-field--readonly {
    .q-field__native {
      cursor: not-allowed !important;
    }

    .q-field__control {
      background-color: $color-neutral-grey-300 !important;
    }
  }

  .q-field {
    &__inner {
      background-color: $color-neutral-white;
      border-radius: 4px;
    }

    &__append {
      height: 44px;
    }

    &__bottom {
      padding: 4px 12px;
    }

    &__control {
      &--readonly {
        background-color: red !important;
      }

      height: 44px !important;
    }

    &__control:before {
      border: none !important;
    }

    &__append .q-field__focusable-action:focus,
    &__append .q-field__focusable-action:focus-visible {
      outline: none;
      border-radius: 9999px;
      box-shadow:
        0 0 0 2px $color-neutral-white,
        0 0 0 4px var(--q-primary) !important;
    }
  }

  .base-button {
    height: 100%;
    width: 30px;
    padding: 0;
    margin: 0;
    border-radius: 4px;
    background-color: $color-neutral-white;

    &:hover {
      background-color: $color-neutral-grey-200;
    }

    &:active {
      background-color: $color-neutral-grey-300;
    }
  }
}

.community,
.consultation,
.project-theme-classic {
  input {
    border: 0 !important;

    &:focus {
      border: 0 !important;
    }
  }
}
</style>
